import React from "react";
import {Modal} from "react-bootstrap";
import {Field, formValueSelector, reduxForm, SubmissionError} from "redux-form";
import ModalHeader from "../ModalHeader";
import ModalBody from "../ModalBody";
import ModalFooter from "../ModalFooter";
import {connectModal} from "redux-modal";
import ReduxLabeledInput from "../../basics/input/labeled-input/ReduxLabeledInput";
import {connect} from "react-redux";
import {withTranslation} from "react-i18next";
import {compose} from "redux";
import moment from "moment";
import {checkIfSettlementOverlaps, dataFormat, getDaysToAddBySectorType} from "../../../utils/SettlementUtils";
import InfoBox from "../../basics/info-box/InfoBox";
import {getBuildingsMap} from "../../../selectors/buildingsSelector";
import {Level} from "../../../constans/levelTypes";
import {
    createSettlement,
    createSettlementNotificationFailure,
    createSettlementNotificationSuccess
} from "../../../api/settlement/create";
import {makeGetCurrentSettlement, SettlementNotSupportedSectorTypes} from "../../../selectors/settlementSelector";
import ReduxLabeledSliderToSelect from "../../redux-mobile-input-swapper/ReduxLabeledSliderToSelect";
import {get} from "lodash";
import ExtraSettlementParams, {extractExtraParams} from "../../extra-settlement-params/ExtraSettlementParams";
import {isUsingFakeData} from "../../../utils/DemoUtils";

export const ModalName = "add-settlement-chamber-modal";

const selector = formValueSelector(ModalName);

function makeMapStateToProps(ownState, ownProps) {
    const params = {}
    const getCurrentSettlement = makeGetCurrentSettlement();
    return function mapStateToProps(state, props) {
        params.CID = props.CID;
        return {
            user: state.user.user,
            currentSettlement: getCurrentSettlement(state, params),
            buildingMap: getBuildingsMap(state),
            settlements: state.settlement.settlement,
            start: selector(state, "start"),
            stop: selector(state, "stop")
        }
    }
}

class AddSettlementChamberModal extends React.Component {

    constructor(props) {
        super(props);
        const {CID, initialize, buildingMap, handleHide, currentSettlement, hideOnNotSupported} = this.props;
        const chamber = buildingMap.get(CID);
        this.changed = {};
        if (!currentSettlement && get(chamber, "level") === Level.CHAMBER) {
            if (!(hideOnNotSupported && SettlementNotSupportedSectorTypes.includes(chamber.SType))) {
                initialize({
                    start: moment().format(moment.HTML5_FMT.DATE),
                    days: this.getDaysToAdd()
                });
                return;
            }
        }
        handleHide();
    }

    getDaysToAdd = () => {
        const {CID, buildingMap, currentSettlement} = this.props;
        const chamber = buildingMap.get(CID);
        let daysToAdd = 0;
        if (chamber && chamber.level === Level.CHAMBER && !currentSettlement) {
            return getDaysToAddBySectorType(chamber.SType)
        }
        return daysToAdd;
    }

    render() {
        const {
            submitting,
            show,
            handleHide,
            handleSubmit,
            t,
            error,
            children,
            canSkip = true
        } = this.props;
        return (
            <Modal onHide={handleHide} show={show} size={"lg"}>
                <form onSubmit={handleSubmit}>
                    <ModalHeader
                        title={t("settings.settlementAdd")}
                        onCloseClick={handleHide}/>
                    <ModalBody>
                        {children}
                        {
                            !!error && <InfoBox boxColor={"error"}>{error}</InfoBox>
                        }
                        <Field
                            name="start"
                            id="start"
                            type="date"
                            label={t("settings.settlementStart")}
                            required
                            component={ReduxLabeledInput}
                        />
                        <Field
                            name="days"
                            id="days"
                            label={t("settings.settlementDays")}
                            required
                            min={1}
                            max={365}
                            component={ReduxLabeledSliderToSelect}
                        />
                        <ExtraSettlementParams t={t}/>
                    </ModalBody>
                    <ModalFooter hasConfirmButton={true} confirmText={t("add")}
                                 onCloseClick={handleHide}
                                 hasCloseButton={canSkip}
                                 submitting={submitting}
                                 closeText={t("skip")}
                                 formName={ModalName}/>
                </form>
            </Modal>
        )
    }
}

const submit = (values, dispatch, props) => {
    const {handleHide, t, CID} = props;
    const {start, days} = values;
    const data = {
        PlcmntID: CID,
        DtaStartTime: +moment.utc(start, moment.HTML5_FMT.DATE).startOf("day"),
        DtaEndTime: +moment.utc(start, moment.HTML5_FMT.DATE).endOf("day").add(days - 1, "days"),
        ...extractExtraParams(values)
    }
    if (isUsingFakeData()) {
        handleHide();
        createSettlementNotificationSuccess();
        return;
    }
    return createSettlement(data).then(() => {
        handleHide();
        createSettlementNotificationSuccess();
    }).catch((e) => {
        if (e.code === 3) {
            throw new SubmissionError({
                _error: t("errors.settlementOverlaps", {
                    count: e.duplicates.length, settlements: e.duplicates.map(s => {
                        return `${dataFormat(s.DtaStartTime)}-${dataFormat(s.DtaEndTime)}`
                    }).join(", ")
                })
            })
        }
        createSettlementNotificationFailure();
    });
}

const validate = (values, props) => {
    const errors = {};
    const {t, settlements, CID, buildingMap} = props;
    const {start, days} = values;
    if (CID) {
        if (SettlementNotSupportedSectorTypes.includes(buildingMap.get(CID)?.SType)) {
            errors._error = t("errors.selectedChamberTypeIsNotSupported", {list: SettlementNotSupportedSectorTypes.map(value => `$t(SType.${value - 1})`).join(", ")});
        }
    }
    if (!start) {
        errors.start = t("required");
    }
    if (Object.keys(errors).length === 0) {
        const data = {
            PlcmntID: CID,
            FTIID: null,
            DtaStartTime: +moment.utc(start, moment.HTML5_FMT.DATE).startOf("day"),
            DtaEndTime: +moment.utc(start, moment.HTML5_FMT.DATE).endOf("day").add(days - 1, "days")
        }
        if (data.DtaEndTime < data.DtaStartTime) {
            errors.stop = `${t("errors.mustBeAbove")} ${t("settings.settlementStart")}`;
            return errors;
        }
        const overlaps = checkIfSettlementOverlaps(data, settlements);
        if (overlaps.length) {
            errors._error = t("errors.settlementOverlaps", {
                count: overlaps.length, settlements: overlaps.map(s => {
                    return `${dataFormat(s.DtaStartTime)}-${dataFormat(s.DtaEndTime)}`
                }).join(", ")
            });
        }
    }
    return errors;
}

export default compose(
    withTranslation(),
    connectModal({name: ModalName}),
    connect(makeMapStateToProps),
    reduxForm({
        form: ModalName,
        onSubmit: submit,
        validate
    }),
)(AddSettlementChamberModal);
